/**
 * Admin Routes
 *
 * Routes for admin functionality: integrations, tenants, users, settings.
 */
import React from 'react';
import { createRoute } from '@tanstack/react-router';
import { createPermissionBeforeLoad } from '@bb/session-manager';
import { authLayoutRoute } from '../rootRoute';
import * as groups from '../../groups.config';
import { AGTableProvider } from '../../contexts';

// Lazy load pages
const Integrations = React.lazy(() => import('../../pages/Integrations/Integrations'));
const IntegrationDetails = React.lazy(() => import('../../pages/IntegrationDetails/IntegrationDetails'));
const Tenants = React.lazy(() => import('../../pages/Tenants/Tenants'));
const NewTenant = React.lazy(() => import('../../pages/NewTenant/NewTenant'));
const Users = React.lazy(() => import('../../pages/Users/Users'));
const Settings = React.lazy(() => import('../../pages/Settings/Settings'));
const Account = React.lazy(() => import('../../pages/Account/Account'));

// Permission checks
const bbAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.BLACKBOILER_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

const companyAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.COMPANY_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

const accountAdminPermission = createPermissionBeforeLoad({
  permissions: [groups.ACCOUNT_ADMIN_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

// ============================================
// INTEGRATIONS
// ============================================
export const integrationsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/integrations',
  beforeLoad: bbAdminPermission,
  component: Integrations,
});

export const integrationDetailsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/integrations/$integrationId',
  beforeLoad: bbAdminPermission,
  component: IntegrationDetails,
});

// ============================================
// TENANTS
// ============================================
export const tenantsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/tenants',
  beforeLoad: bbAdminPermission,
  component: Tenants,
});

export const newTenantRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/tenants/new',
  beforeLoad: bbAdminPermission,
  component: NewTenant,
});

// ============================================
// USERS
// ============================================
/**
 * Users page wrapped with AGTableProvider
 */
const UsersWithProvider = () => (
  <AGTableProvider>
    <Users />
  </AGTableProvider>
);

export const usersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/users',
  beforeLoad: companyAdminPermission,
  component: UsersWithProvider,
});

// ============================================
// SETTINGS
// ============================================
export const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/settings',
  beforeLoad: bbAdminPermission,
  component: Settings,
});

// ============================================
// ACCOUNT
// ============================================
export const accountRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/account',
  beforeLoad: accountAdminPermission,
  component: Account,
});

export const adminRoutes = [
  integrationsRoute,
  integrationDetailsRoute,
  tenantsRoute,
  newTenantRoute,
  usersRoute,
  settingsRoute,
  accountRoute,
];
